<template>
    <section class="configuration-section" :class="{
        'configuration-section--highlighted': isHighlighted,
        'configuration-section--preset': isPresetSection,
        'configuration-section--tenant': isTenantSection,
        'configuration-section--json-error': hasJsonError,
        'configuration-section--unsaved': isSectionDirty
    }" @mousedown="notifyInteraction" @keydown.capture="notifyInteraction" @focusin="notifyInteraction">
        <header class="section-header">
            <div class="section-title-row">
                <h3 class="section-title">
                    <Icon :name="sectionIcon || 'symbol-property'" :size="18" />
                    {{ sectionTitle }}
                </h3>
                <Badge v-if="sectionBadge" :text="sectionBadge.text"
                    :variant="sectionBadge.variant as 'info' | 'error' | 'default' | 'success' | 'warning'" />
                
                <!-- Section-level edit/save controls -->
                <div class="section-actions">
                    <span v-if="!canSwitchView" class="saving-indicator" title="Saving changes...">
                        <Icon name="spinner" :size="12" />
                    </span>
                    
                    <!-- Edit button when not in edit mode -->
                    <Button
                        v-if="canEditThisResource && !isSectionInEditMode"
                        @click="handleEnterEditMode"
                        variant="secondary"
                        size="small"
                        :title="`Edit ${sectionType === 'preset' ? 'preset' : 'tenant'} configuration`"
                    >
                        <Icon name="pencil-simple" :size="14" />
                    </Button>
                    
                    <!-- Save/Cancel segmented control when in edit mode -->
                    <SegmentedControl
                        v-else-if="isSectionInEditMode"
                        v-model="sectionEditAction"
                        :options="sectionEditActionOptions"
                        size="small"
                        @update:model-value="handleSectionEditAction"
                    />
                    
                    <!-- Locked button when no permission -->
                    <Button
                        v-else-if="!canEditThisResource"
                        disabled
                        variant="secondary"
                        size="small"
                        :title="`You don't have permission to edit ${sectionType === 'preset' ? 'presets' : 'tenants'}`"
                    >
                        <Icon name="lock" :size="14" />
                    </Button>
                </div>
                
                <!-- View mode toggle - always visible -->
                <div class="section-view-toggle">
                    <SegmentedControl
                        :model-value="viewMode"
                        :options="sectionViewModeOptions"
                        :disabled="!canSwitchView"
                        size="small"
                        @update:model-value="handleViewModeChange"
                    />
                </div>
            </div>
            <p v-if="sectionDescription" class="section-description">
                {{ sectionDescription }}
            </p>
            <div v-if="precedence !== undefined" class="section-metadata">
                <span class="section-precedence">Precedence: {{ precedence }}</span>
            </div>
        </header>

        <!-- Content wrapper to prevent height jumping during transitions -->
        <div class="section-content-wrapper">
            <!-- Form View -->
            <div class="section-content section-view-item"
                 :class="{ 'view-active': viewMode === 'form' || (viewMode === 'json' && isInitializingEditor) }"
                 :style="{ opacity: isInitializingEditor ? 0.5 : 1 }">
                <!-- Configuration Content for Edit Mode -->
                <div v-if="!isReadonly" class="configuration-content">
                    <TreeView :nodes="configurationTree" :expanded-ids="expandedNodeIds" :selected-id="selectedNodeId"
                        :preset-id="treePresetId" :can-edit="!isReadonly" size="medium" :show-lines="false"
                        @select="handleNodeSelect" @expand="handleNodeExpand" @collapse="handleNodeCollapse"
                        @value-update="handleValueUpdate" @add-child="handleAddChild" @add-root="openAddRoot"
                        @remove="handleRemove" />
                </div>
                <!-- Empty state for readonly -->
                <div v-else-if="configurationTree.length === 0" class="empty-state">
                    <Icon name="info" :size="32" />
                    <p>{{ emptyStateMessage }}</p>
                </div>
                <!-- Show tree view for readonly with content -->
                <div v-else class="configuration-content">
                    <TreeView :nodes="configurationTree" :expanded-ids="expandedNodeIds" :selected-id="selectedNodeId"
                        :preset-id="treePresetId" :can-edit="false" size="medium" :show-lines="false"
                        @select="handleNodeSelect" @expand="handleNodeExpand" @collapse="handleNodeCollapse" />
                </div>
            </div>

            <!-- JSON View (Monaco Editor) - only render when editor is fully ready -->
            <div class="section-content json-editor-content section-view-item"
                 :class="{ 'view-active': viewMode === 'json' && !isInitializingEditor }">
                <div class="monaco-editor-container" :ref="(el) => monacoEditorRef = el as HTMLElement"></div>
            </div>
        </div>

        <AddPropertyModal ref="addModalRef" @add="onModalAdd" />
    </section>
</template>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue'
import AddPropertyModal from '@/components/molecules/AddPropertyModal.vue'
import { Badge, Icon, TreeView, Button, SegmentedControl } from '@/components/atoms'
import type { TreeViewNode } from '@/components/atoms/TreeView.vue'
import { useAuth } from '@/composables/useAuth'
import { usePermissions } from '@/composables/usePermissions'
import { useFieldPermissions } from '@/composables/useFieldPermissions'
import { useConfigurationStore } from '@/stores/configuration'
import { useConfigurationEditor } from '@/composables/useConfigurationEditor'
import { generateJsonPatchOperations } from '@/utils/json-patch'
import type { JsonPatchOperation } from '@/types/configuration'
import { useNotifications } from '@/composables/useNotifications'

// Simple debounce utility
function debounce<T extends (...args: any[]) => void>(fn: T, delay = 300) {
    let timer: ReturnType<typeof setTimeout> | null = null
    return (...args: Parameters<T>) => {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => fn(...args), delay)
    }
}

interface Props {
    // Data source
    configuration: any

    // Section metadata
    sectionType: 'preset' | 'tenant'
    sectionTitle: string
    sectionDescription?: string
    sectionIcon?: string
    sectionBadge?: {
        text: string
        variant: string
    }

    // Context for operations
    presetContext?: {
        presetId: number
        presetName: string
        presetType: string
    }
    tenantId?: string

    // UI state
    precedence?: number
    isHighlighted?: boolean
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  'section-interaction': []
  'dirty-state-changed': [isDirty: boolean]
}>()

// Store and auth
const { canEdit } = useAuth()
const permissions = usePermissions()
const fieldPermissions = useFieldPermissions()
const configStore = useConfigurationStore()

// View mode options for segmented control
const sectionViewModeOptions = [
  { value: 'form', icon: 'tree-structure', tooltip: 'Form View' },
  { value: 'json', icon: 'brackets-curly', tooltip: 'JSON View' }
]

// Section edit action for save/cancel
const sectionEditAction = ref('')
const sectionEditActionOptions = computed(() => [
  { value: 'save', icon: 'floppy-disk', tooltip: isSectionDirty.value ? 'Save changes' : 'No changes to save', disabled: () => !isSectionDirty.value },
  { value: 'cancel', icon: 'trash-simple', tooltip: 'Cancel editing' }
])

// Check if user can edit this specific resource (preset or tenant)
// This checks if user can edit at least ONE field in this specific configuration
const canEditThisResource = computed(() => {
  const resource = props.sectionType === 'preset' ? 'presets' : 'tenants'
  
  // Check for blanket write permission (e.g., "presets:write" or "presets:*")
  if (permissions.canWrite(resource)) {
    return true
  }
  
  // If no configuration data yet, allow edit attempt (fields will be locked individually)
  if (!props.configuration) {
    return permissions.scopes.value.some(scope => {
      const parts = scope.split(':')
      if (parts.length < 2) return false
      const [scopeResource, scopeOperation] = parts
      return (scopeResource === resource || scopeResource === '*') &&
             (scopeOperation === 'write' || scopeOperation === '*')
    })
  }
  
  // Check if user can edit ANY field in this specific configuration
  // Walk through all paths in the configuration and check permissions
  function hasEditableField(obj: any, prefix = ''): boolean {
    if (!obj || typeof obj !== 'object') return false
    
    for (const [key, value] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key
      
      // Check if user can edit this specific path
      if (fieldPermissions.canEditField(resource, path)) {
        return true
      }
      
      // Recursively check nested objects
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        if (hasEditableField(value, path)) {
          return true
        }
      }
    }
    
    return false
  }
  
  return hasEditableField(props.configuration)
})

// Section-level edit mode state
// Track if this specific section is in edit mode (stored in the store to survive re-renders)
const isSectionInEditMode = computed({
  get: () => configStore.isSectionInEditMode(props.sectionType, props.presetContext?.presetId),
  set: (value: boolean) => configStore.setSectionEditMode(props.sectionType, props.presetContext?.presetId, value)
})

// Handle entering edit mode for this section
const handleEnterEditMode = async () => {
  if (!canEditThisResource.value) return
  
  try {
    // Enter section-level edit mode (local state)
    isSectionInEditMode.value = true
    
    // Also trigger global edit mode if not already active
    // This ensures lock state is managed properly
    if (!configStore.isEditMode) {
      // Select the correct tenant/preset context
      if (props.sectionType === 'tenant') {
        configStore.setTenantOverridesSelected(true)
      } else if (props.sectionType === 'preset' && props.presetContext) {
        const presetIndex = configStore.presetHierarchy.findIndex(
          p => p.presetId === props.presetContext!.presetId
        )
        if (presetIndex >= 0) {
          configStore.setSelectedPresetIndex(presetIndex)
        }
      }
      
      await configStore.enterEditMode()
    }
  } catch (err: any) {
    console.error(`Failed to enter edit mode for ${props.sectionType}:`, err)
    isSectionInEditMode.value = false
  }
}

// Handle saving changes for this section only
const handleSaveSectionChanges = async () => {
  if (!isSectionDirty.value) return
  
  try {
    // Filter pending operations to only this section
    const sectionOperations = configStore.pendingOperations.filter(op => {
      if (props.sectionType === 'preset' && props.presetContext) {
        return op.presetContext?.presetId === props.presetContext.presetId
      } else if (props.sectionType === 'tenant') {
        return !op.presetContext // Tenant operations don't have presetContext
      }
      return false
    })
    
    if (sectionOperations.length === 0) {
      console.warn('No operations to save for this section')
      isSectionInEditMode.value = false
      return
    }
    
    // Save only this section's operations
    await configStore.saveSectionChanges(sectionOperations)
    
    // Exit section edit mode
    isSectionInEditMode.value = false
    configStore.setSectionEditMode(
      props.sectionType, 
      props.sectionType === 'preset' ? props.presetContext?.presetId : undefined, 
      false
    )
    
    // Check if we should exit global edit mode
    if (configStore.pendingOperations.length === 0 && 
        !configStore.hasAnySectionsInEditMode && 
        configStore.isEditMode) {
      // Release lock and exit edit mode
      configStore.editorState.lockState = {
        isLocked: false
      }
      configStore.editorState.editMode = false
    }
    
    success(`${props.sectionTitle} changes saved successfully`)
  } catch (err: any) {
    console.error(`Failed to save ${props.sectionType} changes:`, err)
    errorNotification(`Failed to save changes: ${err.message}`)
  }
}

// Handle canceling edit mode for this section
const handleCancelSectionEdit = () => {
  // Remove pending operations for this section
  if (props.sectionType === 'preset' && props.presetContext) {
    configStore.pendingOperations = configStore.pendingOperations.filter(
      op => op.presetContext?.presetId !== props.presetContext!.presetId
    )
  } else if (props.sectionType === 'tenant') {
    configStore.pendingOperations = configStore.pendingOperations.filter(
      op => !!op.presetContext // Keep only preset operations
    )
  }
  
  // Restore this section's configuration from the original snapshot
  // This doesn't require an API call - it uses the snapshot taken when entering edit mode
  if (props.sectionType === 'preset' && props.presetContext) {
    configStore.restoreSectionFromSnapshot('preset', props.presetContext.presetId)
  } else if (props.sectionType === 'tenant') {
    configStore.restoreSectionFromSnapshot('tenant')
  }
  
  // Update dirty state based on remaining operations
  configStore.editorState.isDirty = configStore.pendingOperations.length > 0
  
  // Exit section edit mode and update the store's tracking
  isSectionInEditMode.value = false
  configStore.setSectionEditMode(
    props.sectionType, 
    props.sectionType === 'preset' ? props.presetContext?.presetId : undefined, 
    false
  )
  
  // Only exit global edit mode if BOTH conditions are met:
  // 1. No pending operations remain
  // 2. No sections are still in edit mode
  if (configStore.pendingOperations.length === 0 && 
      !configStore.hasAnySectionsInEditMode && 
      configStore.isEditMode) {
    // Release lock and exit edit mode
    configStore.editorState.lockState = {
      isLocked: false
    }
    configStore.editorState.editMode = false
  }
}

// Handle section edit action from segmented control
const handleSectionEditAction = async (action: string) => {
  if (action === 'save') {
    await handleSaveSectionChanges()
  } else if (action === 'cancel') {
    handleCancelSectionEdit()
  }
  // Reset selection after action
  sectionEditAction.value = ''
}

const { success, error: errorNotification } = useNotifications()

// Track pending form changes and JSON errors
const hasFormPendingChanges = ref(false)
const hasJsonError = ref(false)
const jsonErrorNotificationId = ref<string>()

// Track whether THIS section has unsaved changes
const isSectionDirty = computed(() => {
  // Check if there are pending operations for this specific section
  const hasPendingOps = configStore.pendingOperations.some(op => {
    if (props.presetContext) {
      // For preset sections, check if operation is for this preset
      return op.presetContext?.presetId === props.presetContext.presetId
    } else if (props.sectionType === 'tenant') {
      // For tenant section, check if operation has no preset context (tenant overrides)
      return !op.presetContext
    }
    return false
  })
  
  // Also consider variant selection changes for THIS specific preset level only
  const hasVariantChanges = props.presetContext ? (() => {
    const presetType = props.presetContext.presetType
    const selected = configStore.selectedVariantsByLevel[presetType]?.presetId
    const committed = configStore.committedVariantsByLevel[presetType]?.presetId
    return selected !== committed
  })() : false
  
  return hasPendingOps || hasVariantChanges || hasFormPendingChanges.value
})

// Emit dirty state changes
watch(isSectionDirty, (isDirty) => {
  emit('dirty-state-changed', isDirty)
}, { immediate: true })

// Track if user is actively editing in JSON mode to prevent unwanted updates
const isEditingJson = ref(false)
let editingTimeout: ReturnType<typeof setTimeout> | null = null

// Debounced JSON change handler to create patch operations
const debouncedHandleJsonChange = debounce((value: string) => {
    // Mark that user is editing (prevents external updates to editor)
    isEditingJson.value = true
    if (editingTimeout) clearTimeout(editingTimeout)
    editingTimeout = setTimeout(() => {
        isEditingJson.value = false
    }, 1000) // Keep flag active for 1s after last change
    
    try {
        const newConfig = JSON.parse(value)

        // Clear error state if previously had error
        if (hasJsonError.value) {
            hasJsonError.value = false
            jsonErrorNotificationId.value = undefined
            // Show success notification briefly
            success('JSON syntax is now valid', {
                timeout: 3000
            })
        }

        // Generate patch operations from old to new configuration
        const operations = generateJsonPatchOperations(props.configuration || {}, newConfig)

        // Add each operation to the store with the appropriate context
        operations.forEach((op: JsonPatchOperation) => {
            configStore.addPatchOperation(op, props.presetContext)
        })

        console.log(`[ConfigurationSection] Generated ${operations.length} patch operations from JSON changes`)
    } catch (err) {
        // Invalid JSON - don't create patch operations
        console.warn('[ConfigurationSection] Invalid JSON, skipping patch generation:', err)
        
        // Set error state and show notification
        if (!hasJsonError.value) {
            hasJsonError.value = true
            const errorMessage = err instanceof Error ? err.message : String(err)
            jsonErrorNotificationId.value = errorNotification(`Invalid JSON: ${errorMessage}`, {
                timeout: 0, // Persist until resolved
                source: props.sectionTitle
            })
        }
    }
}, 500)

// Computed properties (must be before composable to pass as ref)
// Section is readonly if user can't edit it OR if this section is not in edit mode
const isReadonly = computed(() => !canEdit.value || !isSectionInEditMode.value)

// Generate section key for view mode tracking (matches edit mode key format)
const sectionKey = computed(() => 
  props.sectionType === 'preset' ? `preset:${props.presetContext?.presetId}` : 'tenant'
)

// Configuration editor composable (Monaco + view mode)
const {
    viewMode,
    monacoEditor,
    monacoEditorRef,
    hasPendingChanges,
    isInitializingEditor,
    setViewMode,
    updateJsonContent
} = useConfigurationEditor({
    initialViewMode: configStore.globalSectionViewMode,
    presetId: props.presetContext?.presetId.toString(),
    tenantId: props.tenantId,
    sectionKey: sectionKey.value,
    isReadonly: isReadonly, // Pass the computed ref
    onJsonChange: debouncedHandleJsonChange
})

// Wrapper for setViewMode to handle string type from SegmentedControl
const handleViewModeChange = (mode: string) => {
  setViewMode(mode as 'form' | 'json')
}

// Additional computed properties
const isPresetSection = computed(() => props.sectionType === 'preset')
const canSwitchView = computed(() => !hasPendingChanges.value && !hasFormPendingChanges.value)
const isTenantSection = computed(() => props.sectionType === 'tenant')

const treePresetId = computed(() => {
    if (isPresetSection.value && props.presetContext) {
        return props.presetContext.presetId.toString()
    }
    return isTenantSection.value ? 'tenant' : ''
})

const emptyStateMessage = computed(() => {
    if (isPresetSection.value) {
        return `No ${props.presetContext?.presetType || 'preset'}-specific configuration`
    }
    return 'No tenant-specific configuration'
})

// TreeView state
const selectedNodeId = ref<string>()
const expandedNodeIds = ref<string[]>([])

// Check if user can add root-level properties (requires full edit access, not path-restricted)
const canAddRootProperties = computed(() => {
    if (isReadonly.value) return false
    const resource = props.sectionType === 'preset' ? 'presets' : 'tenants'
    return fieldPermissions.hasFullEditAccess(resource)
})

// Configuration tree - convert flat configuration to tree structure
const configurationTree = computed(() => {
    if (!props.configuration) {
        // If no configuration and can add root properties, show add root sentinel
        if (canAddRootProperties.value) {
            return [{
                id: '__add_root__',
                label: 'Add',
                icon: 'plus',
                data: {
                    isAddRoot: true,
                    isGroup: false,
                    key: '',
                    label: 'Add',
                    value: null,
                    type: 'add',
                    readonly: false,
                    path: ''
                }
            } as TreeViewNode]
        }
        return []
    }

    const tree = buildConfigurationTree(props.configuration, '', isReadonly.value)

    // Add root add sentinel only if user has full edit access (can add new top-level properties)
    if (canAddRootProperties.value) {
        tree.push({
            id: '__add_root__',
            label: 'Add',
            icon: 'plus',
            data: {
                isAddRoot: true,
                isGroup: false,
                key: '',
                label: 'Add',
                value: null,
                type: 'add',
                readonly: false,
                path: ''
            }
        } as TreeViewNode)
    }

    return tree
})

// Auto-expand all tree nodes when configuration changes
watch(configurationTree, (newTree: TreeViewNode[]) => {
    if (newTree.length > 0) {
        const allNodeIds = getAllNodeIds(newTree)
        expandedNodeIds.value = allNodeIds
    }
}, { immediate: true })

// Sync configuration to JSON editor when switching to JSON mode
watch(viewMode, (newMode) => {
    if (newMode === 'json' && props.configuration) {
        const jsonString = JSON.stringify(props.configuration, null, 2)
        updateJsonContent(jsonString)
    }
})

// Update Monaco editor readonly state when edit mode or permissions change
watch(isReadonly, (newReadonly) => {
    console.log('[ConfigurationSection] isReadonly watcher triggered:', {
        newReadonly,
        hasEditor: !!monacoEditor.value,
        viewMode: viewMode.value,
        canEdit: canEdit.value,
        editMode: configStore.isEditMode
    })
    
    if (monacoEditor.value && viewMode.value === 'json') {
        monacoEditor.value.updateOptions({ readOnly: newReadonly })
        console.log('[ConfigurationSection] Monaco readonly updated to:', newReadonly)
    }
}, { immediate: true })

// Sync configuration to JSON editor when it changes (only if already in JSON mode)
// BUT: Skip updates if user is actively editing to prevent cursor jumping
watch(() => props.configuration, (newConfig) => {
    // Don't update editor if user is currently typing/editing
    if (isEditingJson.value) {
        console.log('[ConfigurationSection] Skipping config update - user is editing JSON')
        return
    }
    
    if (newConfig && viewMode.value === 'json') {
        console.log('[ConfigurationSection] Configuration changed:', {
            timeout: newConfig.database?.timeout,
            timeoutType: typeof newConfig.database?.timeout
        })
        const jsonString = JSON.stringify(newConfig, null, 2)
        updateJsonContent(jsonString)
    }
}, { deep: true, immediate: true })

// Helper function to get all node IDs for expansion
function getAllNodeIds(nodes: TreeViewNode[]): string[] {
    const ids: string[] = []

    function traverse(nodeList: TreeViewNode[]) {
        for (const node of nodeList) {
            // Only expand container nodes (objects/arrays), not primitive values or add sentinels
            if (node.data?.type === 'object' || node.data?.type === 'array') {
                ids.push(node.id)
            }
            if (node.children && node.children.length > 0) {
                traverse(node.children)
            }
        }
    }

    traverse(nodes)
    return ids
}

// Helper function to build tree from configuration object with path-based permissions
function buildConfigurationTree(config: Record<string, any>, prefix = '', globalReadonly = false): TreeViewNode[] {
    if (!config) return []

    const nodes: TreeViewNode[] = []
    const resource = props.sectionType === 'preset' ? 'presets' : 'tenants'

    // Create tree nodes for each top-level key
    for (const [key, value] of Object.entries(config)) {
        const nodeId = prefix ? `${prefix}.${key}` : key
        
        // Check path-specific permission for this node
        // If globally readonly OR user lacks permission for this path, mark as readonly
        const isNodeReadonly = globalReadonly || !fieldPermissions.canEditField(resource, nodeId)

        if (Array.isArray(value)) {
            // Array container
            const arrayChildren: TreeViewNode[] = value.map((item, index) => {
                const childId = `${nodeId}.${index}`
                const itemType = typeof item === 'boolean' ? 'boolean' : typeof item === 'number' ? (Number.isInteger(item) ? 'integer' : 'number') : typeof item === 'string' ? 'string' : Array.isArray(item) ? 'array' : 'object'
                // Check permission for array child
                const isChildReadonly = globalReadonly || !fieldPermissions.canEditField(resource, childId)
                
                return {
                    id: childId,
                    label: String(index),
                    icon: getPropertyIcon(itemType),
                    value: item,
                    children: typeof item === 'object' && !Array.isArray(item) ? buildConfigurationTree(item, childId, globalReadonly) : undefined,
                    data: {
                        isGroup: false,
                        key: childId,
                        label: String(index),
                        value: item,
                        type: itemType,
                        readonly: isChildReadonly,
                        path: childId
                    }
                } as TreeViewNode
            })



            nodes.push({
                id: nodeId,
                label: key,
                icon: getPropertyIcon('array'),
                children: arrayChildren,
                data: {
                    isGroup: true,
                    key: nodeId,
                    label: key,
                    value: value,
                    type: 'array',
                    readonly: isNodeReadonly,
                    path: nodeId
                }
            } as TreeViewNode)
        } else if (value && typeof value === 'object') {
            // Object container (nested group)
            const children = buildConfigurationTree(value, nodeId, globalReadonly)

            nodes.push({
                id: nodeId,
                label: key,
                icon: getPropertyIcon('object'),
                children: children,
                data: {
                    isGroup: true,
                    key: nodeId,
                    label: key,
                    value: value,
                    type: 'object',
                    readonly: isNodeReadonly,
                    path: nodeId
                }
            } as TreeViewNode)
        } else {
            // Primitive value
            const valueType = typeof value === 'boolean' ? 'boolean' : typeof value === 'number' ? (Number.isInteger(value) ? 'integer' : 'number') : 'string'
            nodes.push({
                id: nodeId,
                label: key,
                icon: getPropertyIcon(valueType),
                data: {
                    isGroup: false,
                    key: nodeId,
                    label: key,
                    value: value,
                    type: valueType,
                    readonly: isNodeReadonly,
                    path: nodeId
                }
            } as TreeViewNode)
        }
    }

    return nodes
}

function getPropertyIcon(type: string): string {
    switch (type) {
        case 'boolean': return 'symbol-boolean'
        case 'number':
        case 'integer': return 'symbol-numeric'
        case 'string': return 'symbol-string'
        case 'array': return 'symbol-array'
        case 'object': return 'symbol-object'
        default: return 'symbol-property'
    }
}

// Modal state
const addModalRef = ref<InstanceType<typeof AddPropertyModal> | null>(null)
const addContext = ref<{
    parentPath: string
    isArray: boolean
} | null>(null)

// Event handlers
const notifyInteraction = () => {
    emit('section-interaction')
}

const handleNodeSelect = (nodeId: string) => {
    selectedNodeId.value = nodeId
}

const handleNodeExpand = (nodeId: string) => {
    if (!expandedNodeIds.value.includes(nodeId)) {
        expandedNodeIds.value.push(nodeId)
    }
}

const handleNodeCollapse = (nodeId: string) => {
    const index = expandedNodeIds.value.indexOf(nodeId)
    if (index > -1) {
        expandedNodeIds.value.splice(index, 1)
    }
}

const handleValueUpdate = (nodeId: string, value: any) => {
    if (isReadonly.value) return

    // Mark that we have pending form changes
    hasFormPendingChanges.value = true

    // Debounce the actual update
    debouncedValueUpdate(nodeId, value)
}

const debouncedValueUpdate = debounce((nodeId: string, value: any) => {
    // Convert nodeId (dot notation) to JSON Pointer path
    const jsonPointerPath = '/' + nodeId.split('.').join('/')

    // Create operation
    const operation = {
        op: 'replace',
        path: jsonPointerPath,
        value: value
    }

    // Add to store with appropriate context
    if (isPresetSection.value && props.presetContext) {
        configStore.addPatchOperation(operation, props.presetContext)
    } else if (isTenantSection.value) {
        configStore.addPatchOperation(operation)
    }

    // Clear pending changes flag after save completes
    hasFormPendingChanges.value = false
}, 300)

const handleAddChild = (payload: { nodeId: string; node: TreeViewNode }) => {
    if (isReadonly.value) return

    // Check if it's an array by looking at the data.type from the node
    const isArray = payload.node.data?.type === 'array'

    // Set up add context
    addContext.value = {
        parentPath: payload.nodeId,
        isArray: isArray
    }

    // Get existing keys for the parent
    const existingKeys = getExistingKeysForPath(payload.nodeId)

    // Open modal
    addModalRef.value?.open(payload.nodeId, existingKeys, isArray)
}

const openAddRoot = () => {
    if (isReadonly.value) return

    addContext.value = {
        parentPath: '',
        isArray: false
    }

    const existingKeys = Object.keys(props.configuration || {})
    addModalRef.value?.open('', existingKeys, false)
}

const handleRemove = (payload: { nodeId: string; node: TreeViewNode }) => {
    if (isReadonly.value) return

    const jsonPointerPath = '/' + payload.nodeId.split('.').join('/')

    const operation = {
        op: 'remove',
        path: jsonPointerPath
    }

    // Add to store with appropriate context
    if (isPresetSection.value && props.presetContext) {
        configStore.addPatchOperation(operation, props.presetContext)
    } else if (isTenantSection.value) {
        configStore.addPatchOperation(operation)
    }
}

const onModalAdd = (e: { key?: string; value: any; type: string; parentPath: string }) => {
    if (!addContext.value) return

    const isArray = addContext.value.isArray
    const newKey = e.key || '0'

    // Expand parent container
    ensureExpanded(addContext.value.parentPath)

    // Convert dot notation path to JSON Pointer format
    const jsonPointerParent = e.parentPath ? '/' + e.parentPath.split('.').join('/') : ''
    const path = isArray
        ? `${jsonPointerParent}/-`
        : `${jsonPointerParent}/${newKey}`

    // Create the operation
    const operation = {
        op: 'add',
        path: path,
        value: e.value
    }

    // Add to store with appropriate context
    if (isPresetSection.value && props.presetContext) {
        configStore.addPatchOperation(operation, props.presetContext)
    } else if (isTenantSection.value) {
        configStore.addPatchOperation(operation)
    }

    // Focus the newly added node
    if (!isArray && newKey) {
        const newId = addContext.value.parentPath ? `${addContext.value.parentPath}.${newKey}` : newKey
        setTimeout(() => { selectedNodeId.value = newId }, 0)
    } else if (isArray) {
        const parentPath = addContext.value.parentPath
        if (parentPath) {
            try {
                const segs = parentPath.split('.')
                let arr: any = props.configuration
                for (const s of segs) {
                    if (arr == null) break
                    arr = arr[s]
                }
                if (Array.isArray(arr)) {
                    const newIndex = arr.length - 1
                    const newId = `${parentPath}.${newIndex}`
                    setTimeout(() => { selectedNodeId.value = newId }, 0)
                }
            } catch { }
        }
    }
}

// Helper functions
const ensureExpanded = (path: string) => {
    if (path && !expandedNodeIds.value.includes(path)) {
        expandedNodeIds.value.push(path)
    }
}

const getExistingKeysForPath = (path: string): string[] => {
    if (!props.configuration) return []

    if (!path) {
        return Object.keys(props.configuration)
    }

    const pathParts = path.split('.')
    let current = props.configuration

    for (const part of pathParts) {
        if (current && typeof current === 'object' && part in current) {
            current = current[part]
        } else {
            return []
        }
    }

    return current && typeof current === 'object' ? Object.keys(current) : []
}

// Cleanup on unmount
onUnmounted(() => {
    if (editingTimeout) {
        clearTimeout(editingTimeout)
        editingTimeout = null
    }
})
</script>

<style scoped lang="scss">
.configuration-section {
    background: var(--vscode-editor-background);
    border-radius: 8px;
    border: 1px solid var(--vscode-widget-border);
    box-shadow: 0 1px 3px 0 var(--vscode-widget-shadow);
    /* Smooth transition for height changes when switching view modes */
    transition: all 0.3s ease-in-out;
    /* overflow: visible to allow content to display fully */
}

.configuration-section--highlighted {
    /* Match sidebar preset-item--selected styling for visual consistency */
    outline: 2px solid var(--vscode-focusBorder);
    outline-offset: 0;
    box-shadow: 0 0 0 1px var(--vscode-focusBorder);
    border-color: var(--vscode-focusBorder);
}

.configuration-section--unsaved {
    /* Visual indicator for unsaved changes in this section */
    outline: 2px solid var(--vscode-unsavedBorder) !important;
    border-color: var(--vscode-unsavedBorder) !important; 

    .section-header {
        color: var(--vscode-unsavedBorder) !important;
        border-color: var(--vscode-unsavedBorder) !important; 
    }
    .badge {
        background-color: var(--vscode-unsavedBorder) !important;
    }
}

.configuration-section--unsaved.configuration-section--highlighted {
    outline-color: var(--vscode-unsavedBorder) !important;

    .section-header {
        color: var(--vscode-unsavedBorder) !important;
        border-color: var(--vscode-unsavedBorder) !important; 
    }
    .badge {
        background-color: var(--vscode-unsavedBorder) !important;
    }
}

.configuration-section--json-error {
    outline-color: var(--negative);
    /* box-shadow: 0 0 0 1px var(--vscode-editorWarning-foreground); */
}

.section-header {
    padding: 12px 16px;
    border-bottom: 1px solid var(--vscode-widget-border);
}

.section-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.section-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--vscode-foreground);
    display: flex;
    align-items: center;
    margin: 0;
    gap: 8px;
}

.section-description {
    font-size: 0.875rem;
    color: var(--vscode-descriptionForeground);
    margin: 6px 0;
}

.section-metadata {
    font-size: 0.75rem;
    color: var(--vscode-descriptionForeground);
}

.section-content-wrapper {
    position: relative;
    min-height: 0;
}

.section-content {
    padding: 12px 16px;
}

.section-view-item {
    /* No transition by default - inactive views hide instantly */
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
}

.section-view-item:not(.view-active) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
}

.section-view-item.view-active {
    /* Only the active view gets a fade-in transition */
    transition: opacity 0.075s ease-out;
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    position: relative; /* Active view takes up space */
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 0;
    color: var(--vscode-descriptionForeground);
}

.empty-state p {
    margin-top: 8px;
    font-size: 0.875rem;
}

.empty-state-edit {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 0;
    color: var(--vscode-descriptionForeground);
}

.empty-state-edit p {
    margin-top: 8px;
    margin-bottom: 16px;
    font-size: 0.875rem;
}

.add-first-property-btn {
    background: var(--vscode-button-background);
    color: var(--vscode-button-foreground);
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.add-first-property-btn:hover {
    background: var(--vscode-button-hoverBackground);
}

.add-first-property-btn:focus {
    outline: 2px solid var(--vscode-focusBorder);
    outline-offset: 2px;
}

/* Section actions (edit buttons) */
.section-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-left: auto;
}

/* View toggle buttons */
.section-view-toggle {
    display: flex;
    gap: 8px;
    align-items: center;
}

.saving-indicator {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--vscode-descriptionForeground);
    padding: 0 8px;
    animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
}

/* JSON editor content */
.json-editor-content {
    padding: .43rem !important;
    line-height: 0;
}

.monaco-editor-container {
    width: 100%;
    /* Height is set dynamically by JavaScript based on content */
    /* min-height removed - let JS control the height fully based on content */
    max-height: 1200px;
    border: 1px solid var(--vscode-widget-border);
    border-radius: 0 0 4px 4px;
    overflow: hidden; /* Clip any overflow to prevent extra space */
    transition: height 0.05s ease-in-out;
    margin: 0;
    display: block;
    line-height: normal;
}

/* Monaco initialization loading state */
.monaco-initializing {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100px;
    padding: 24px;
}

.loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid var(--vscode-progressBar-background);
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>